// =============================================================================
// RBAC config — shared document: role→module permissions + per-user role
// overrides. localStorage cache (instant paint) reconciled with the shared
// backend (/rbac-config) so Admin edits reach every user on their next load.
// `normalizeRbac` is pure (unit-tested): fills missing roles from defaults and
// auto-adds newly-shipped modules to each role's matrix.
// =============================================================================

import { RbacConfig, RolePermissions, EDITABLE_ROLES, ModuleId } from '../types/auth';
import { DEFAULT_ROLE_PERMISSIONS } from './users';

const STORAGE_RBAC = 'flameco.rbac.config.v1';

// Union of every module any role can access by default — the "all modules known
// at this version" snapshot stored alongside the config.
export function currentKnownModules(): ModuleId[] {
  const set = new Set<ModuleId>();
  for (const role of EDITABLE_ROLES) {
    for (const m of DEFAULT_ROLE_PERMISSIONS[role] ?? []) set.add(m);
  }
  return [...set];
}

export const DEFAULT_RBAC_CONFIG: RbacConfig = {
  rolePermissions: DEFAULT_ROLE_PERMISSIONS,
  userRoleOverrides: {},
  knownModules: currentKnownModules(),
};

// Merge a stored (possibly older/partial) config over the defaults:
//  - a role missing entirely (e.g. a newly-added role like Manager) is seeded
//    from its default matrix;
//  - a module that is in a role's default but did NOT exist last time the config
//    was saved (not in knownModules) is appended — so new tools appear without
//    wiping an admin's explicit removals.
export function normalizeRbac(stored: Partial<RbacConfig> | null | undefined): RbacConfig {
  const storedPerms = (stored?.rolePermissions ?? {}) as Partial<RolePermissions>;
  const known = new Set<ModuleId>(stored?.knownModules ?? []);
  const rolePermissions = {} as RolePermissions;

  for (const role of EDITABLE_ROLES) {
    const def = DEFAULT_ROLE_PERMISSIONS[role] ?? [];
    const current = storedPerms[role];
    if (!current) {
      rolePermissions[role] = [...def];   // new/unknown role → seed from defaults
      continue;
    }
    const additions = def.filter(m => !known.has(m) && !current.includes(m));
    rolePermissions[role] = additions.length ? [...current, ...additions] : [...current];
  }

  return {
    rolePermissions,
    userRoleOverrides: stored?.userRoleOverrides ?? {},
    knownModules: currentKnownModules(),
  };
}

export function loadRbac(): RbacConfig {
  if (typeof localStorage === 'undefined') return normalizeRbac(null);
  const raw = localStorage.getItem(STORAGE_RBAC);
  if (!raw) return normalizeRbac(null);
  try {
    return normalizeRbac(JSON.parse(raw) as Partial<RbacConfig>);
  } catch {
    return normalizeRbac(null);
  }
}

export function saveRbac(cfg: RbacConfig): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_RBAC, JSON.stringify(cfg));
}

// Roles that must never be demoted via an override, to prevent an admin lockout:
// any user whose LOGIN-DERIVED base role is Admin stays Admin regardless of the
// override map. (Overrides may still PROMOTE a non-admin.) Enforced in RoleContext.
export { EDITABLE_ROLES };
